import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LayoutGrid,
  Sparkles,
  Wand2,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Search,
  ListFilter,
  Folder,
  Inbox,
  Star,
  Globe,
  Eraser,
  CopyCheck,
  SlidersHorizontal,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import { gradientStyle } from "./gradients";

type Tool = "spaces" | "smartlists" | "clutter";

const TOOLS: { key: Tool; icon: LucideIcon }[] = [
  { key: "spaces", icon: LayoutGrid },
  { key: "smartlists", icon: ListFilter },
  { key: "clutter", icon: Wand2 },
];

const CHROME_URL: Record<Tool, string> = {
  spaces: "markflow.fyi/space/inbox",
  smartlists: "markflow.fyi/list/dev-reading",
  clutter: "markflow.fyi/duplicates",
};

export function HomeControlCenter() {
  const { t } = useTranslation();
  const [active, setActive] = useState<Tool>('spaces');

  return (
    <section
      id="tools"
      className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20"
    >
      <div className="mx-auto mb-10 max-w-2xl text-center">
        <span className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-violet-600 dark:text-violet-400">
          {t("home.controlCenter.eyebrow")}
        </span>
        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl dark:text-zinc-50">
          {t("home.controlCenter.title")}
        </h2>
        <p className="mt-3 text-base leading-relaxed text-zinc-600 sm:text-lg dark:text-zinc-400">
          {t("home.controlCenter.subtitle")}
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        {/* tool selector — compact tab row on mobile, rich vertical cards on lg */}
        <div className="grid grid-cols-3 gap-2 lg:flex lg:flex-col lg:gap-3">
          {TOOLS.map(({ key, icon: Icon }) => {
            const isActive = key === active;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActive(key)}
                aria-pressed={isActive}
                className={cn(
                  "group relative overflow-hidden rounded-2xl border p-3 text-center transition-all lg:p-4 lg:pl-5 lg:text-left",
                  isActive
                    ? "border-violet-300 bg-violet-50 shadow-[0_14px_40px_-22px_rgba(139,92,246,0.7)] dark:border-violet-500/40 dark:bg-violet-500/10"
                    : "border-zinc-200 bg-white hover:bg-zinc-50 dark:border-white/10 dark:bg-white/[0.02] dark:hover:bg-white/[0.05]",
                )}
              >
                {isActive && (
                  <span
                    aria-hidden
                    className="absolute inset-x-3 bottom-0 h-[3px] rounded-full brand-gradient lg:inset-x-auto lg:inset-y-3 lg:left-0 lg:h-auto lg:w-[3px]"
                  />
                )}
                <div className="flex flex-col items-center gap-2 lg:flex-row lg:items-center lg:gap-3">
                  <span
                    className={cn(
                      "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl",
                      isActive
                        ? "brand-gradient text-white shadow-[0_6px_18px_rgba(139,92,246,0.4)]"
                        : "bg-zinc-100 text-zinc-500 dark:bg-white/[0.04] dark:text-zinc-300",
                    )}
                  >
                    <Icon className="h-5 w-5" aria-hidden />
                  </span>
                  <span className="font-display text-xs font-semibold leading-tight text-zinc-900 lg:text-base dark:text-zinc-50">
                    {t(`home.controlCenter.tools.${key}.title`)}
                  </span>
                </div>
                <p className="mt-2.5 hidden text-sm leading-relaxed text-zinc-600 lg:block dark:text-zinc-400">
                  {t(`home.controlCenter.tools.${key}.copy`)}
                </p>
              </button>
            );
          })}
        </div>

        {/* app window — decorative product preview. The interactive tool buttons
            (siblings above) carry the real, accessible feature titles + copy, so
            the mock UI here is aria-hidden to keep its fake meta (URL, counts,
            rule ops…) out of the a11y tree. NOTE: aria-hidden does NOT exempt
            text from the axe color-contrast audit — low-vision sighted users
            still see it — so the muted meta uses AA-passing light-mode colors
            (zinc-500 on white, zinc-600 on gray fills; not zinc-400) (MF-205). */}
        <div aria-hidden className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(20,20,26,0.9),rgba(13,13,17,0.9))] dark:shadow-[0_40px_110px_-50px_rgba(0,0,0,0.9)]">
          {/* chrome — fake browser frame */}
          <div className="flex items-center gap-2 border-b border-zinc-200 px-4 py-3 dark:border-white/[0.06]">
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-300 dark:bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-300 dark:bg-white/15" />
            <span className="h-2.5 w-2.5 rounded-full bg-zinc-300 dark:bg-white/15" />
            <div className="ml-2 flex h-7 max-w-[340px] flex-1 items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-100 px-2.5 dark:border-white/[0.06] dark:bg-white/[0.04]">
              <Search className="h-3 w-3 text-zinc-400" aria-hidden />
              <span className="truncate text-xs text-zinc-600 dark:text-zinc-400">
                {CHROME_URL[active]}
              </span>
            </div>
          </div>

          {/* body — re-keyed per tool so entrance + item animations replay on switch */}
          <div key={active} className="mf-rise min-h-[470px] p-5 sm:p-6">
            {active === "spaces" && <SpacesPanel />}
            {active === "smartlists" && (
              <SmartListsPanel
                name={t("home.controlCenter.panels.smartlists.name")}
                subtitle={t("home.controlCenter.panels.smartlists.subtitle")}
                match={t("home.controlCenter.panels.smartlists.match")}
                summary={t("home.controlCenter.panels.smartlists.summary", {
                  count: 14,
                })}
              />
            )}
            {active === "clutter" && (
              <ClutterPanel
                found={t("home.controlCenter.panels.clutter.found", {
                  count: 12,
                })}
                subtitle={t("home.controlCenter.panels.clutter.subtitle")}
                cleaned={t("home.controlCenter.panels.clutter.cleaned", {
                  removed: 11,
                  kept: 1,
                })}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Spaces & Deep Folders ───────────────────────────────────────────────── */

// Mirrors the real MarkFlow sidebar (workspace switcher + primary nav + folders).
const SPACE_NAV: { icon: LucideIcon; label: string; count?: string; active?: boolean; tint?: boolean }[] = [
  { icon: LayoutGrid, label: 'All Bookmarks', count: '1,018', active: true },
  { icon: Star, label: 'Favorites' },
  { icon: Inbox, label: 'Unsorted', count: '10' },
  { icon: Globe, label: 'All Spaces' },
  { icon: Eraser, label: 'Cleanup', tint: true },
  { icon: CopyCheck, label: 'Duplicates', tint: true },
];

const SPACE_FOLDERS: {
  label: string;
  count: string;
  indent: number;
  chevron?: boolean;
}[] = [
  { label: "AI", count: "288", indent: 4, chevron: true },
  { label: "Tools", count: "26", indent: 20 },
  { label: "Shopping", count: "130", indent: 4 },
  { label: "Personal", count: "108", indent: 4 },
];

const SPACE_CARDS = [
  { from: "#3a7d2c", to: "#7bc24a", letter: "L", title: "Linear roadmap" },
  { from: "#5b3da6", to: "#9b6bd8", letter: "N", title: "Notion HQ" },
  { from: "#1f7a4d", to: "#3fc07d", letter: "F", title: "Figma files" },
  { from: "#1d3a8a", to: "#5a7fe0", letter: "S", title: "Slack archive" },
  { from: "#8a1d4e", to: "#d8567f", letter: "G", title: "GitHub repos" },
  { from: "#7a5a1d", to: "#d8a64a", letter: "V", title: "Vercel deploys" },
];

function SpacesPanel() {
  return (
    <div className="grid gap-5 sm:grid-cols-[230px_1fr]">
      {/* sidebar — mirrors the real app */}
      <div className="space-y-1">
        {/* workspace switcher */}
        <div className="mb-2 flex items-center justify-between rounded-xl border border-zinc-200 bg-white px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.03]">
          <span className="flex items-center gap-2 text-sm font-semibold text-zinc-900 dark:text-white">
            <Inbox className="h-4 w-4 text-violet-500" aria-hidden />
            Inbox
          </span>
          <ChevronDown className="h-4 w-4 text-zinc-400" aria-hidden />
        </div>

        {/* primary nav */}
        <ul className="space-y-0.5">
          {SPACE_NAV.map((n) => (
            <li
              key={n.label}
              className={cn(
                "flex items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-sm",
                n.active
                  ? "bg-zinc-100 font-medium text-zinc-900 dark:bg-white/[0.06] dark:text-white"
                  : "text-zinc-600 dark:text-zinc-300",
              )}
            >
              <n.icon
                className={cn(
                  "h-4 w-4 shrink-0",
                  n.tint ? "text-violet-500" : "text-zinc-400",
                )}
                aria-hidden
              />
              <span className="flex-1">{n.label}</span>
              {n.count && (
                <span className="text-xs text-zinc-600 dark:text-zinc-400">{n.count}</span>
              )}
            </li>
          ))}
        </ul>

        {/* folders header */}
        <div className="flex items-center gap-1.5 px-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:text-zinc-400">
          <ChevronDown className="h-3 w-3" aria-hidden />
          <span>Folders</span>
          <span>37</span>
          <span className="ml-auto flex items-center gap-2">
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            <Plus className="h-3.5 w-3.5" aria-hidden />
          </span>
        </div>

        {/* folders */}
        <ul className="space-y-0.5">
          {SPACE_FOLDERS.map((f) => (
            <li
              key={f.label}
              className="flex items-center gap-1.5 py-1.5 pr-1 text-sm text-zinc-600 dark:text-zinc-300"
              style={{ paddingLeft: f.indent }}
            >
              {f.chevron ? (
                <ChevronRight
                  className="h-3 w-3 shrink-0 text-zinc-400"
                  aria-hidden
                />
              ) : (
                <span className="w-3 shrink-0" />
              )}
              <Folder
                className="h-3.5 w-3.5 shrink-0 text-zinc-400"
                aria-hidden
              />
              <span className="flex-1">{f.label}</span>
              <span className="text-xs text-zinc-500 dark:text-zinc-400">{f.count}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* main */}
      <div>
        <div className="mb-4 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <h3 className="font-display text-lg font-bold text-zinc-900 dark:text-white">
            All Bookmarks
          </h3>
          <span className="text-xs text-zinc-500 dark:text-zinc-400">
            1,018 items · across every space
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {SPACE_CARDS.map((c) => (
            <div
              key={c.letter}
              className="overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-white/10 dark:bg-[#121216]"
            >
              <div
                className="flex h-12 items-center px-3 font-display text-lg font-bold text-white"
                style={gradientStyle(c)}
              >
                {c.letter}
              </div>
              <div className="truncate px-3 py-2 text-xs font-medium text-zinc-600 dark:text-zinc-300">
                {c.title}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Smart Lists (replaces the disabled AI-categorization showcase) ─────── */

// Real rule grammar (MF-156/165): tag has · domain is · similar_to text (semantic).
const SL_RULES: { field: string; op?: string; value: string; semantic?: boolean }[] = [
  { field: 'Tag', op: 'has', value: 'reading' },
  { field: 'Domain', op: 'is', value: 'github.com' },
  { field: 'Similar to', value: 'rust async', semantic: true },
];

const SL_RESULTS: { title: string; domain: string; fav: string }[] = [
  { title: "tokio/tokio", domain: "github.com", fav: "#2563eb" },
  { title: "rust-lang/async-book", domain: "github.com", fav: "#ce422b" },
  { title: "smol-rs/smol", domain: "github.com", fav: "#7c3aed" },
];

function SmartListsPanel({
  name,
  subtitle,
  match,
  summary,
}: {
  name: string;
  subtitle: string;
  match: string;
  summary: string;
}) {
  return (
    <div className="space-y-4">
      {/* header */}
      <div className="flex items-center gap-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg brand-gradient text-white shadow-[0_0_18px_rgba(139,92,246,0.5)]">
          <ListFilter className="h-4 w-4" aria-hidden />
        </span>
        <div>
          <div className="text-sm font-semibold text-zinc-900 dark:text-white">
            {name}
          </div>
          <div className="text-xs text-zinc-500 dark:text-zinc-400">
            {subtitle}
          </div>
        </div>
      </div>

      {/* rule builder */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-3.5 dark:border-white/10 dark:bg-white/[0.02]">
        <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-500 dark:text-zinc-400">
          {match}
        </div>
        <div className="space-y-2">
          {SL_RULES.map((r) => (
            <div
              key={r.field + r.value}
              className="flex flex-wrap items-center gap-1.5 text-xs"
            >
              <span className="rounded-md bg-zinc-100 px-2 py-1 font-medium text-zinc-700 dark:bg-white/[0.06] dark:text-zinc-200">
                {r.field}
              </span>
              {r.op && <span className="text-zinc-500 dark:text-zinc-400">{r.op}</span>}
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium",
                  r.semantic
                    ? "border border-violet-500/30 bg-violet-500/10 text-violet-600 dark:text-violet-200"
                    : "bg-zinc-100 text-zinc-700 dark:bg-white/[0.06] dark:text-zinc-200",
                )}
              >
                {r.semantic && <Sparkles className="h-3 w-3" aria-hidden />}
                {r.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* matching results — auto-updating */}
      <div>
        <div className="mb-2 px-1 text-xs text-zinc-500 dark:text-zinc-400">
          {summary}
        </div>
        <ul className="space-y-2">
          {SL_RESULTS.map((r, i) => (
            <li
              key={r.title}
              className="mf-fly flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 dark:border-white/10 dark:bg-white/[0.02]"
              style={{ animationDelay: `${0.12 + i * 0.1}s` }}
            >
              <span
                className="h-7 w-7 shrink-0 rounded-lg"
                style={{ background: r.fav }}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">
                  {r.title}
                </div>
                <div className="truncate text-[11px] text-zinc-500 dark:text-zinc-400">
                  {r.domain}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ── Clutter Control ─────────────────────────────────────────────────────── */

const DUP_FAV = 'linear-gradient(135deg,#f43f5e,#fb923c)';
const KEPT_ROW = { title: 'Getting started guide', meta: 'example.com · Apr 20, 2026' };
const DUP_ROWS = [
  {
    title: "Getting started guide",
    meta: "example.com · Apr 21, 2026",
    delay: "0.2s",
  },
  {
    title: "Getting started guide (imported)",
    meta: "example.com · Apr 20, 2026",
    delay: "0.35s",
  },
  {
    title: "Getting started guide (imported)",
    meta: "example.com · Apr 21, 2026",
    delay: "0.5s",
  },
];

function ClutterPanel({
  found,
  subtitle,
  cleaned,
}: {
  found: string;
  subtitle: string;
  cleaned: string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-500/15 text-violet-600 ring-1 ring-violet-500/30 dark:text-violet-200">
          <Wand2 className="h-4 w-4" aria-hidden />
        </span>
        <span className="font-display text-base font-bold text-zinc-900 dark:text-white">
          {found}
        </span>
      </div>
      <p className="pl-[42px] text-sm text-zinc-500 dark:text-zinc-400">
        {subtitle}
      </p>

      <div className="overflow-hidden rounded-2xl border border-zinc-200 dark:border-white/10">
        {/* table header */}
        <div className="flex items-center justify-between gap-2 border-b border-zinc-200 bg-zinc-50 px-4 py-3 dark:border-white/[0.06] dark:bg-white/[0.02]">
          <span className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-300">
            Keep
            <span className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2 py-1 text-xs font-semibold text-zinc-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-zinc-100">
              Oldest
              <ChevronDown className="h-3 w-3" aria-hidden />
            </span>
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-lg brand-gradient px-3.5 py-1.5 text-xs font-bold text-white shadow-[0_6px_18px_rgba(139,92,246,0.35)]">
            <Check className="h-3.5 w-3.5" strokeWidth={2.4} aria-hidden />
            Merge
          </span>
        </div>

        {/* kept row */}
        <div className="flex items-center gap-3 bg-emerald-500/[0.06] px-4 py-3">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-emerald-950">
            <Check className="h-3 w-3" strokeWidth={3} aria-hidden />
          </span>
          <span
            className="h-5 w-5 shrink-0 rounded-md"
            style={{ backgroundImage: DUP_FAV }}
          />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold text-zinc-900 dark:text-white">
              {KEPT_ROW.title}
            </div>
            <div className="text-[11px] text-zinc-500 dark:text-zinc-400">{KEPT_ROW.meta}</div>
          </div>
          <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
            Keep
          </span>
        </div>

        {/* collapsing duplicates */}
        {DUP_ROWS.map((d, i) => (
          <div
            key={i}
            className="mf-collapse flex items-center gap-3 border-t border-zinc-200 px-4 py-3 dark:border-white/5"
            style={{ animationDelay: d.delay }}
          >
            <span className="h-5 w-5 shrink-0 rounded-full border-2 border-zinc-300 dark:border-zinc-600" />
            <span
              className="h-5 w-5 shrink-0 rounded-md"
              style={{ backgroundImage: DUP_FAV }}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-zinc-600 dark:text-zinc-300">
                {d.title}
              </div>
              <div className="text-[11px] text-zinc-500 dark:text-zinc-400">{d.meta}</div>
            </div>
            <span className="text-xs text-zinc-500 dark:text-zinc-400">Delete</span>
          </div>
        ))}
      </div>

      {/* success */}
      <div className="mf-win flex items-center gap-2.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-200">
        <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden />
        {cleaned}
      </div>
    </div>
  );
}
